import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiMail, FiAlertCircle } from 'react-icons/fi';
import { getUsers, createUser, updateUser, deleteUser } from '../services/userService';
import UserModal from '../components/UserModal';
import ConfirmModal from '../components/ConfirmModal';

const TeamPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUsers();
      setUsers(data.users || data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load team members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-primary-100 text-primary-800';
      case 'manager':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };
  
  const handleOpenUserModal = (user = null) => {
    setCurrentUser(user);
    setIsUserModalOpen(true);
  };
  
  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setCurrentUser(null);
  };
  
  const handleOpenConfirmModal = (user) => {
    setUserToDelete(user);
    setIsConfirmModalOpen(true);
  };
  
  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setUserToDelete(null);
  };
  
  const handleSaveUser = async (userData) => {
    try {
      setIsSaving(true);
      if (currentUser && currentUser.id) {
        // Update existing user
        const updatedUser = await updateUser(currentUser.id, userData);
        setUsers(users.map(user => user.id === updatedUser.id ? updatedUser : user));
      } else {
        // Create new user
        const newUser = await createUser(userData);
        setUsers([...users, newUser]);
      }
      handleCloseUserModal();
    } catch (err) {
      console.error('Error saving user:', err);
      // Handle error (could add error state and display message)
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteUser(userToDelete.id);
      setUsers(users.filter(user => user.id !== userToDelete.id));
      handleCloseConfirmModal();
    } catch (err) {
      console.error('Error deleting user:', err);
      // Handle error
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-secondary-900">Team Members</h1>
        <button 
          className="btn btn-primary flex items-center"
          onClick={() => handleOpenUserModal()}
        >
          <FiPlus className="mr-1" />
          Add Team Member
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded relative mb-4">
          <div className="flex items-center">
            <FiAlertCircle className="mr-2" size={18} />
            <span>{error}</span>
          </div>
          <button 
            className="mt-2 btn btn-secondary"
            onClick={fetchUsers}
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-card overflow-hidden">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-secondary-500">
                    No team members found. Click "Add Team Member" to create one.
                  </td>
                </tr>
              ) : users.map((user) => (
                <tr key={user.id} className="hover:bg-secondary-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-200 flex items-center justify-center text-primary-700 font-medium">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-secondary-900">{user.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-secondary-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClass(user.role)}`}>
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-primary-600 hover:text-primary-900 mr-3">
                      <FiMail size={18} />
                    </button>
                    <button 
                      className="text-secondary-600 hover:text-secondary-900 mr-3"
                      onClick={() => handleOpenUserModal(user)}
                    >
                      <FiEdit2 size={18} />
                    </button>
                    <button 
                      className="text-danger hover:text-red-700"
                      onClick={() => handleOpenConfirmModal(user)}
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* User Modal */}
      <UserModal
        isOpen={isUserModalOpen}
        onClose={handleCloseUserModal}
        user={currentUser}
        onSave={handleSaveUser}
        isSaving={isSaving}
      />
      
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleDeleteUser}
        title="Delete Team Member"
        message={userToDelete ? `Are you sure you want to delete ${userToDelete.name}? This action cannot be undone.` : ''}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TeamPage;